import type { CSSProperties } from "react";
import {
  CheckIcon,
  InfoIcon,
  PANEL,
  PlayIcon,
  StatusPill,
  TrendUpIcon,
} from "../../../shared/components";
import type { ActivityDay, AttendanceState } from "../../../shared/api/parent";
import type { Skill } from "../../../shared/types/prisma-enums";
import { gameArtFor, skillTint } from "./Charts";
import { SKILL_LABEL } from "./skillSections";
import { PARENT_COPY, type Lang } from "./copy";

/**
 * A day of a child's play, drawn once and used in two places.
 *
 * 🔴 IT REPLACED A THREE-COLUMN TABLE, AND THE TABLE WAS ANSWERING THE WRONG
 * QUESTION. C2's "Recent activity" printed one row per EVENT — a date, a game
 * name and "NUMERACY L2" — so a single afternoon filled the screen with forty
 * near-identical lines and a parent scrolled past their child's week without
 * seeing a day in it. What a parent asks is *"what did they do on Tuesday"*, and
 * that is a day, not an event.
 *
 * 🔴 AND IT IS THE SAME COMPONENT THE CALENDAR OPENS. C6 draws twenty-eight
 * squares — a control shaped like a button — and until now a tap on one had
 * nothing to say beyond the colour the square already had. Both screens now show
 * the identical card, from the identical `GET .../activity` fold, so a parent who
 * taps Tuesday on the calendar and scrolls to Tuesday in the feed cannot be shown
 * two different Tuesdays.
 *
 * ⚠️ THE PLAY AND THE REGISTER ARE KEPT VISIBLY APART. A register is the
 * school's record of attendance; the games are the child's own play, which
 * happens at home as often as at school. "No register taken" beside "played Sky
 * Quest for twelve minutes" is not a contradiction, and the layout must not let
 * it read as one.
 */

/* ────────────────────────────────────────────────────────────── the date ── */

/**
 * "Tuesday, 12 August" — and "Today" for the one day a parent is living in.
 *
 * ⚠️ PARSED AS UTC AND FORMATTED IN UTC. The key is an IST calendar day written
 * `YYYY-MM-DD`; letting `toLocaleDateString` apply the browser's offset would
 * label the square a day earlier for any reader west of Greenwich — which is
 * every parent of this product travelling.
 */
export function formatDayLabel(key: string, lang: Lang, today: string): string {
  const t = PARENT_COPY[lang];
  if (key === today) return t.activityToday;

  const yesterday = new Date(
    new Date(`${today}T00:00:00.000Z`).getTime() - 86_400_000,
  )
    .toISOString()
    .slice(0, 10);
  if (key === yesterday) return t.activityYesterday;

  return new Date(`${key}T00:00:00.000Z`).toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  });
}

/** The IST day the reader is living in. The calendar's own "today". */
export function istToday(now: Date = new Date()): string {
  return new Date(now.getTime() + 330 * 60_000).toISOString().slice(0, 10);
}

/* ──────────────────────────────────────────────────────── one day's play ── */

/**
 * What a child played on one day.
 *
 * ⚠️ THE COUNTS COME BEFORE THE GAMES, because the first thing a parent wants is
 * "was this a lot or a little" and the second is "of what". Both are facts; only
 * the order is a design decision.
 */
export function DayPlay({ day, lang }: { day: ActivityDay; lang: Lang }) {
  const t = PARENT_COPY[lang];

  return (
    <div className="flex flex-col gap-3">
      <p className="flex flex-wrap items-center gap-2">
        {day.events > 0 && (
          <StatusPill tone="neutral" icon={<PlayIcon size={14} />}>
            {t.activityRounds(day.events)}
          </StatusPill>
        )}
        {day.minutes > 0 && (
          /* 🔴 "At least" — a sitting nothing ever closed contributes nothing, so
             this is a floor rather than a measurement. `ActivityDay.minutes` says
             so on the wire and the pill says so on screen. */
          <StatusPill tone="neutral" icon={<InfoIcon size={14} />}>
            {t.activityMinutes(day.minutes)}
          </StatusPill>
        )}
        {day.correct + day.wrong > 0 && (
          /* 🔴 THE FRACTION, NEVER THE PERCENTAGE. Contract D4, and the reason
             the server ships `correct` and `answered` rather than a ratio. */
          <StatusPill tone="success" icon={<CheckIcon size={14} />}>
            {t.activityAnswers(day.correct, day.correct + day.wrong)}
          </StatusPill>
        )}
        {day.levelUps > 0 && (
          <StatusPill tone="warning" icon={<TrendUpIcon size={14} />}>
            {t.activityLevelUps(day.levelUps)}
          </StatusPill>
        )}
        {/* ⚠️ A SITTING WITH NO EVENTS IS STILL SOMETHING. `recorded: false` (I5)
            writes a session and no events, and so does a child who opened the app
            and left — a card that said nothing at all would be contradicting a
            row that exists. */}
        {day.events === 0 && day.sessions > 0 && (
          <StatusPill tone="neutral" icon={<InfoIcon size={14} />}>
            {t.activityOpenedOnly}
          </StatusPill>
        )}
      </p>

      {day.games.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {day.games.map((row) => {
            const art = gameArtFor(row.game);
            return (
              <li
                key={row.game}
                className="flex items-center gap-2.5 rounded-lg border border-line py-1.5 pr-3 pl-1.5"
                style={{
                  borderColor: art
                    ? `color-mix(in srgb, ${art.tint} 35%, transparent)`
                    : "var(--color-line)",
                }}
              >
                {art ? (
                  <img
                    src={art.art}
                    alt=""
                    aria-hidden="true"
                    className="h-8 w-8 shrink-0 rounded-md object-cover select-none"
                    loading="lazy"
                    draggable={false}
                  />
                ) : null}
                <span className="flex min-w-0 flex-col">
                  {/* 🔴 THE GAME'S NAME, NOT ITS ID. `gameArt.ts` exists because
                      the reports table was printing "sky-quest" at a head
                      teacher; a day card would have done it to a parent. An id
                      this build cannot name keeps the id, which is honest. */}
                  <span className="truncate font-dash text-dash-xs font-bold text-ink">
                    {art ? art.title[lang] : row.game}
                  </span>
                  {row.topLevel !== null && (
                    <span className="font-dash text-dash-xs text-muted">
                      {t.activityLevel(row.topLevel)}
                    </span>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {day.skills.length > 0 && (
        <p className="flex flex-wrap items-center gap-x-4 gap-y-1.5 font-dash text-dash-xs text-muted">
          {day.skills.map((row) => (
            <span key={row.skill} className="flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className="inline-block h-2 w-2 shrink-0 rounded-full"
                style={{ background: skillTint(row.skill) }}
              />
              {skillLabel(row.skill, lang)}
            </span>
          ))}
        </p>
      )}
    </div>
  );
}

/* ───────────────────────────────────────────────────────── the day feed ── */

export interface ActivityTimelineProps {
  /** Oldest first, as the server sends them. This reverses. */
  days: readonly ActivityDay[];
  lang: Lang;
  today: string;
}

/**
 * The recent-activity feed, newest day first.
 *
 * ⚠️ EMPTY DAYS ARE SKIPPED HERE AND KEPT IN THE CHART. A chart needs the zeroes
 * — a gap reads as missing data — and a feed does not: nineteen cards saying
 * "nothing" is a feed a parent stops scrolling.
 */
export function ActivityTimeline({ days, lang, today }: ActivityTimelineProps) {
  const t = PARENT_COPY[lang];
  const played = [...days]
    .reverse()
    .filter((d) => d.events > 0 || d.sessions > 0);

  if (played.length === 0) {
    return (
      <div className={`${PANEL} p-5`}>
        <p className="text-dash-sm text-muted">{t.activityEmpty}</p>
      </div>
    );
  }

  return (
    <ul className="flex flex-col gap-3">
      {played.map((day, i) => (
        <li
          key={day.day}
          className={`${PANEL} play-rise p-4`}
          style={{ "--rise-i": Math.min(i, 6) } as CSSProperties}
        >
          <p className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
            <span className="font-dash text-dash-md font-extrabold text-ink">
              {formatDayLabel(day.day, lang, today)}
            </span>
            {day.sessions > 0 && (
              <span className="tabular font-dash text-dash-xs text-muted">
                {t.activitySessions(day.sessions)}
              </span>
            )}
          </p>
          <DayPlay day={day} lang={lang} />
        </li>
      ))}
    </ul>
  );
}

/* ──────────────────────────────────────────────────── the calendar's day ── */

export interface DayDetailProps {
  /** `YYYY-MM-DD`. */
  dayKey: string;
  lang: Lang;
  today: string;
  /** Undefined while the activity read is still in flight or was never made. */
  activity: ActivityDay | undefined;
  /** What the register said. Null when the calendar has no state for the day. */
  state: AttendanceState | null;
  holidayName: string | null;
  onClose: () => void;
}

/**
 * Everything known about one day, opened from a square on the calendar.
 *
 * 🔴 TWO RECORDS, TWO HEADINGS, AND THE SEPARATION IS THE HONEST PART. The
 * register belongs to the school and the play belongs to the child; a day can
 * carry either, both or neither, and the four combinations all mean something
 * different. Merging them into one sentence — "was at school and played" — would
 * be the screen inferring a relationship neither table records.
 *
 * ⚠️ A FUTURE DAY SAYS SO. A 28-square grid ends on today, so this can only
 * happen if a clock is wrong — and "nothing recorded" for tomorrow reads as bad
 * news rather than as a date that has not arrived.
 */
export function DayDetail({
  dayKey,
  lang,
  today,
  activity,
  state,
  holidayName,
  onClose,
}: DayDetailProps) {
  const t = PARENT_COPY[lang];
  const label = formatDayLabel(dayKey, lang, today);
  const future = dayKey > today;
  const played =
    activity !== undefined && (activity.events > 0 || activity.sessions > 0);

  return (
    <div className={`${PANEL} mt-4 p-5`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h3 className="font-dash text-dash-lg font-extrabold text-ink">
          {label}
        </h3>
        <button
          type="button"
          onClick={onClose}
          className="rounded-pill px-3 py-1.5 font-dash text-dash-xs font-bold text-ink-soft transition-colors duration-150 ease-dash hover:bg-canvas hover:text-ink"
        >
          {t.dayClose}
        </button>
      </div>

      {future ? (
        <p className="mt-3 text-dash-sm text-muted">{t.dayNotYet}</p>
      ) : (
        <div className="mt-4 flex flex-col gap-5">
          {state !== null && (
            <section>
              <h4 className="mb-2 font-dash text-dash-xs font-bold tracking-[0.08em] text-muted uppercase">
                {t.daySchoolSaid}
              </h4>
              <p>
                {/* §12 — icon + colour + text, all three, which `StatusPill`'s own
                    type requires rather than merely recommends. */}
                <StatusPill
                  tone={registerTone(state)}
                  icon={<InfoIcon size={14} />}
                >
                  {registerLabel(state, holidayName, lang)}
                </StatusPill>
              </p>
            </section>
          )}

          <section>
            <h4 className="mb-2 font-dash text-dash-xs font-bold tracking-[0.08em] text-muted uppercase">
              {t.dayTheyPlayed}
            </h4>
            {played && activity ? (
              <DayPlay day={activity} lang={lang} />
            ) : (
              <>
                <p className="text-dash-sm text-ink">{t.dayNoPlay}</p>
                {/* 🔴 THE SENTENCE THAT STOPS A BLANK HALF READING AS A FAILURE.
                    A child is not expected to play every day, and a parent
                    looking at an empty Tuesday should not be left to decide
                    whether that is bad. */}
                <p className="mt-1 text-dash-xs text-muted">
                  {t.dayNoPlayHint}
                </p>
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

/* ───────────────────────────────────────────────────────────────── shared ── */

/**
 * The register's four states, in the calendar's own words.
 *
 * ⚠️ THE STRINGS ARE THE LEGEND'S, NOT NEW ONES. The grid below already teaches
 * a parent what each colour means; a day panel that phrased the same four states
 * differently would be two vocabularies for one fact.
 */
function registerLabel(
  state: AttendanceState,
  holidayName: string | null,
  lang: Lang,
): string {
  const t = PARENT_COPY[lang];
  switch (state) {
    case "ABSENT":
      return t.attendanceLegendAbsent;
    case "PRESENT":
      return t.attendanceLegendPresent;
    case "HOLIDAY":
      return holidayName ?? t.attendanceLegendHoliday;
    default:
      /* 🔴 "Nobody took the register" — never "present". A register nobody took
         writes no row, which is identical in the data to a day the whole class
         came in. */
      return t.attendanceLegendUnknown;
  }
}

function registerTone(
  state: AttendanceState,
): "success" | "warning" | "neutral" {
  switch (state) {
    case "PRESENT":
      return "success";
    /* ⚠️ WARNING, NOT DANGER. `AttendanceScreen` refuses `tone="danger"` on the
       count for a stated reason — *"three absences is a fact about a term, not an
       alarm about a family"* — and one day's panel is not the place to break it. */
    case "ABSENT":
      return "warning";
    default:
      return "neutral";
  }
}

/**
 * A skill's own name.
 *
 * ⚠️ `SKILL_LABEL` ALREADY EXISTS AND IS THE ONLY COPY. `skillSections.ts` owns
 * it — *"localised here so the components stay dumb"* — and a second table in a
 * view file is how one skill ends up with two names on two screens of the same
 * tier.
 */
export const skillLabel = (skill: Skill, lang: Lang): string =>
  SKILL_LABEL[skill][lang];
